import { every, isEmpty, } from "lodash";
import React, { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import * as Api from "../../../api";
import { TApiSubset } from "../../../api/endpoints/Subset";
import { selectParameters } from "../../../store/selectors/new/parameters";
import { selectSubsetOptions } from "../../../store/selectors/new/subsetOptions";
import { formatArea, formatBaseTime, formatHindcastDay, formatLevelist, formatRun, } from "../../../utils/Format";
import EnsembleMembers from "../../ProductFormatters/EnsembleMembers";
import ParamLevelist from "../../ProductFormatters/ParamLevelist";
import ParamQuantile from "../../ProductFormatters/ParamQuantile";
import Run from "../../ProductFormatters/Run";
import Steps from "../../ProductFormatters/Steps";
import TimeStep from "../../ProductFormatters/TimeStep";
import { IProductFormValues } from "../ProductForm";
import SubsetInfo from "../ProductForm/controls/SubsetInfo";

interface IProps {
  values: IProductFormValues | null;
  subset?: TApiSubset | null;
}

const PreviewItem = (props: {
  title: string;
  children: ReactNode;
}) => {
  const { title, children } = props;

  return (
    <div style={{ marginBottom: '1em' }}>
      <h5>{title}</h5>
      <div>
        {children}
      </div>
    </div>
  )
}

const ConfigurationPreview = (props: IProps) => {
  const { values } = props;
  const availableParams = useSelector(selectParameters);
  const [subset, setSubset] = useState<TApiSubset | null>(props.subset ?? null);
  const params = useParams<{ subsetId: string }>();
  const subsetOptions = useSelector(selectSubsetOptions(subset?.id));

  useEffect(() => {
    if (params.subsetId) {
      Api.Subset.fetch(params.subsetId).then(subset => {
        setSubset(subset);
      });
    }
  }, [params.subsetId])


  if (!values || !subset || !subsetOptions) {
    return null;
  }

  return (
    <div>
      <h3 className="heading">Selected Configuration</h3>

      {
        every(Object.values(values), isEmpty) &&
        <div className="empty">Nothing selected yet.</div>
      }

      {
        <PreviewItem title="Set">
          <SubsetInfo subset={subset} />
        </PreviewItem>
      }

      {
        Boolean(values.name) &&
        <PreviewItem title="Name">
          {values.name}
        </PreviewItem>
      }

      {
        Boolean(values.time.length) &&
        <PreviewItem title="Forecast runs">
          {values.time.map(formatBaseTime).join(', ')}
        </PreviewItem>
      }

      {
        Boolean(values.steps.length) &&
        <PreviewItem title="Forecast time steps">
          <Steps values={values.steps} bcType={subset.bc_type} selectedTimes={values.time} />
        </PreviewItem>
      }

      {
        Boolean(values.time_step.length) &&
        <PreviewItem title="Forecast time steps">
          <TimeStep values={values.time_step} />
        </PreviewItem>
      }

      {
        Boolean(values.use_step.length) &&
        <PreviewItem title="Forecast time steps">
          <Run values={values.use_step} as="div" />
        </PreviewItem>
      }

      {
        Boolean(values.number.length) &&
        <PreviewItem title="Ensemble members">
          <EnsembleMembers values={values.number} as="div" />
        </PreviewItem>
      }

      {
        Boolean(values.areas.length) &&
        <PreviewItem title="Areas">
          {
            values.areas.map(v => {
              const formatted = formatArea(v);

              return <div key={formatted}>{formatted}</div>
            })
          }
        </PreviewItem>
      }

      {
        Boolean(values.grid.length) &&
        <PreviewItem title="Resolution">
          {values.grid.join(', ')}
        </PreviewItem>
      }

      {
        Boolean(values.location) &&
        <PreviewItem title="Number of points (locations)">
          {values.location!.toString()}
        </PreviewItem>
      }

      {
        Boolean(values.use.length) &&
        <PreviewItem title="Runs">
          {values.use.sort().map(v => formatRun(Number(v))).join(', ')}
        </PreviewItem>
      }

      {
        values.hindcast && values.hindcast.length &&
        <PreviewItem title="Hindcast">
          {values.hindcast.sort().map(v => formatHindcastDay(Number(v))).join(', ')}
        </PreviewItem>
      }

      {
        Boolean(values.fcmonth.length) &&
        <PreviewItem title="Forecast months">
          {values.fcmonth.join(', ')}
        </PreviewItem>
      }

      {
        Boolean(values.channel.length) &&
        <PreviewItem title="Satellite channels">
          {values.channel.join(', ')}
        </PreviewItem>
      }

      {
        Boolean(values.direction.length) &&
        <PreviewItem title="Directions">
          {values.direction.join(', ')}
        </PreviewItem>
      }

      {
        Boolean(values.frequency.length) &&
        <PreviewItem title="Frequencies">
          {values.frequency.join(', ')}
        </PreviewItem>
      }

      {
        Boolean(values.param.length) &&
        <PreviewItem title="Parameters">
          {values.param.join(', ')}
        </PreviewItem>
      }

      {
        Boolean(values.levelist.length) &&
        <PreviewItem title="Levels">
          {formatLevelist(values.levelist)}
        </PreviewItem>
      }

      {
        Boolean(values.param_levelist.length) &&
        <PreviewItem title="Parameters">
          <ParamLevelist
            value={values.param_levelist.map(param => ({ ...param, level: parseInt(param.level, 10) }))}
            params={availableParams}
          />
        </PreviewItem>
      }

      {
        Boolean(values.param_quantile.length) &&
        <PreviewItem title="Parameters">
          <ParamQuantile value={values.param_quantile} params={availableParams} />
        </PreviewItem>
      }
    </div>
  )
}

export default ConfigurationPreview;
